// Deterministic layout for the strategy flowchart.
//
// Kept separate from the renderer so the geometry is unit-testable and the SVG
// component stays a pure projection of it. The layout is niche-agnostic: it
// positions whatever phases and transitions the strategy declares and never
// looks at their names.
//
// Phases are stacked top-to-bottom in their configured order. Transitions
// between neighbouring phases run straight down the spine; transitions that
// skip ahead are routed through lanes on the right; transitions that go
// backwards (re-engagement, returning to an earlier step) are routed on the
// left. That split is what keeps a dense graph readable — the eye can follow
// the spine and treat side lanes as exceptions.

import type { GraphEdge, GraphNode, StrategyGraph } from "@/lib/types";

export const NODE_W = 300;
/** Tall enough for the title row, slug, goal line and the badge row together. */
export const NODE_H = 104;
export const V_GAP = 64;
export const H_GAP = 32;
export const LANE_GAP = 30;
/**
 * Minimum distance from the phase column to the first side lane. The real
 * offset is derived per layout from the widest side label (see
 * `laneOffsetFor`), so lane lines never run through label text however long
 * the transition's label is; this floor keeps short-labelled graphs compact
 * and stable.
 */
export const LANE_OFFSET = 172;
/** Gap between a phase's edge and the side labels anchored beside it. */
export const LABEL_GAP = 10;
/** Air between the far end of a side label and the lane line beyond it. */
export const LANE_CLEARANCE = 12;
export const MARGIN = 24;
/** Corner radius where an orthogonal edge turns. */
export const CORNER_R = 10;
/**
 * Vertical spread between labels of several transitions leaving one phase.
 * Must exceed a label chip's own height or the chips overlap — a phase with
 * three exits is common, not an edge case.
 */
export const LABEL_STAGGER = 28;

export type EdgeRoute = "spine" | "forward" | "back" | "self" | "dangling";

/**
 * What a transition *means* in the flow, which is what the colour encodes:
 * carrying on to the next phase, skipping ahead, going back, or pointing at
 * nothing. Derived from the graph's shape, never from any phase's name.
 */
export type EdgeCategory = "advance" | "branch" | "return" | "broken";

const CATEGORY_BY_ROUTE: Record<EdgeRoute, EdgeCategory> = {
  spine: "advance",
  forward: "branch",
  back: "return",
  self: "return",
  dangling: "broken",
};

export interface LayoutNode extends GraphNode {
  x: number;
  y: number;
  w: number;
  h: number;
  layer: number;
}

export interface LayoutEdge {
  key: string;
  from: string;
  to: string | null;
  label: string;
  description: string | null;
  kind: GraphEdge["kind"];
  priority: number;
  setStatus: string | null;
  requiresHumanApproval: boolean;
  dangling: boolean;
  route: EdgeRoute;
  category: EdgeCategory;
  path: string;
  labelX: number;
  labelY: number;
  /** Which side the label sits on, so the renderer can anchor it outward. */
  labelAnchor: "start" | "middle" | "end";
}

export interface GraphLayout {
  width: number;
  height: number;
  nodes: LayoutNode[];
  edges: LayoutEdge[];
}

/** The chip renders its label at this size; the status suffix one point smaller. */
const LABEL_FONT_PX = 11;
const STATUS_FONT_PX = 10;
/** Average advance of a sans-serif glyph as a fraction of the font size. */
const AVG_GLYPH_EM = 0.6;
/** Horizontal padding + border of the chip, in px. */
const CHIP_CHROME_PX = 18;
/** An inline icon (⏸ / ⚠) plus the flex gap before it. */
const ICON_PX = 18;

/**
 * Estimated on-screen width of a transition's label chip, in px. Labels are
 * written out in full — the operator reads the transition rules straight off
 * the chart — so the layout has to know roughly how wide each one is to keep
 * lanes clear of the text. Layout runs without a DOM, hence an estimate; it
 * errs wide rather than tight.
 */
export function estimateLabelWidth(
  edge: Pick<LayoutEdge, "label" | "setStatus" | "requiresHumanApproval" | "dangling">,
): number {
  let width = CHIP_CHROME_PX + edge.label.length * LABEL_FONT_PX * AVG_GLYPH_EM;
  if (edge.setStatus) {
    // Rendered as "· STATUS" after a flex gap.
    width += 4 + (edge.setStatus.length + 2) * STATUS_FONT_PX * AVG_GLYPH_EM;
  }
  if (edge.requiresHumanApproval) width += ICON_PX;
  if (edge.dangling) width += ICON_PX;
  return Math.ceil(width);
}

/** Lane offset that keeps a lane line clear of the widest label beside it. */
function laneOffsetFor(widestLabel: number): number {
  return Math.max(LANE_OFFSET, LABEL_GAP + widestLabel + LANE_CLEARANCE);
}

/**
 * Group phases into layers by their configured order. Distinct orders become
 * successive layers; phases that share an order (only reachable in an unsaved
 * draft — the validator rejects duplicates on save) sit side by side.
 */
function assignLayers(nodes: GraphNode[]): GraphNode[][] {
  const sorted = [...nodes].sort(
    (a, b) => a.order - b.order || a.slug.localeCompare(b.slug),
  );
  const layers: GraphNode[][] = [];
  let currentOrder: number | null = null;
  for (const node of sorted) {
    const current = layers[layers.length - 1];
    if (current === undefined || node.order !== currentOrder) {
      layers.push([node]);
      currentOrder = node.order;
    } else {
      current.push(node);
    }
  }
  return layers;
}

/** Operator-dragged node positions, keyed by phase slug (2.8). */
export type PositionOverrides = Record<string, { x: number; y: number }>;

export function layoutGraph(
  graph: StrategyGraph,
  overrides?: PositionOverrides,
): GraphLayout {
  const layers = assignLayers(graph.nodes);
  const widest = layers.reduce((max, layer) => Math.max(max, layer.length), 1);
  const contentW = widest * NODE_W + (widest - 1) * H_GAP;

  // Reserve room for however many side lanes the edges will actually need, so
  // the drawing never clips and the SVG can scale to fit its container.
  const routes = classifyRoutes(graph, layers);
  const leftLanes = countLanes(routes, "back");
  const rightLanes = countLanes(routes, "forward") + (hasRoute(routes, "self") ? 1 : 0);
  const hasDangling = hasRoute(routes, "dangling");

  // Side labels sit beside their source phase and are shown in full, so each
  // side's lanes start beyond its widest label rather than at a fixed distance.
  const leftOffset = laneOffsetFor(widestLabel(graph, routes, ["back"]));
  const rightOffset = laneOffsetFor(
    widestLabel(graph, routes, ["forward", "self", "dangling"]),
  );

  const leftPad = MARGIN + (leftLanes > 0 ? leftOffset + leftLanes * LANE_GAP : 0);
  const rightPad =
    MARGIN +
    (rightLanes > 0
      ? rightOffset + rightLanes * LANE_GAP
      : hasDangling
        ? rightOffset
        : 0);

  const nodes: LayoutNode[] = [];
  layers.forEach((layer, layerIndex) => {
    const layerW = layer.length * NODE_W + (layer.length - 1) * H_GAP;
    const startX = leftPad + (contentW - layerW) / 2;
    layer.forEach((node, indexInLayer) => {
      nodes.push({
        ...node,
        x: startX + indexInLayer * (NODE_W + H_GAP),
        y: MARGIN + layerIndex * (NODE_H + V_GAP),
        w: NODE_W,
        h: NODE_H,
        layer: layerIndex,
      });
    });
  });

  // Dragged positions replace the computed ones after lane reservation, so
  // edges connect wherever the node actually sits while the lane columns keep
  // their derived homes. Clamped to the margin so nothing is dragged off-canvas.
  if (overrides) {
    for (const node of nodes) {
      const o = overrides[node.slug];
      if (!o || !Number.isFinite(o.x) || !Number.isFinite(o.y)) continue;
      node.x = Math.max(0, o.x);
      node.y = Math.max(0, o.y);
    }
  }

  const byslug = new Map(nodes.map((n) => [n.slug, n]));
  const spineRight = leftPad + contentW;

  // Lanes are assigned by vertical span, shortest innermost: a hop over one
  // phase hugs the column while an intake-to-archive run takes an outer lane.
  // Nested brackets cross far less than lanes doled out in declaration order,
  // and the assignment is stable under edge reordering in the YAML.
  const laneByEdgeIndex = new Map<number, number>();
  (["forward", "back"] as const).forEach((side) => {
    graph.edges
      .map((edge, index) => ({ edge, index }))
      .filter(({ index }) => routes[index] === side)
      .map(({ edge, index }) => {
        const s = byslug.get(edge.from)!;
        const t = byslug.get(edge.to!)!;
        return { index, span: Math.abs(t.y - s.y) };
      })
      .sort((a, b) => a.span - b.span || a.index - b.index)
      .forEach(({ index }, position) => laneByEdgeIndex.set(index, position + 1));
  });
  // Side-routed labels are anchored beside their source phase rather than out
  // on the lane. Lanes are ~30px apart but a label is ~150px wide, so lane-side
  // labels overlap as soon as a phase has more than one of them; beside the
  // source they inherit the row spacing instead, and this counter fans out the
  // several transitions that leave the same phase.
  const sideLabelsPerSource = new Map<string, number>();
  // How many side labels each phase will end up with, known before placing any,
  // so a phase's labels can be centred on it instead of drifting downward.
  const sideTotalPerSource = new Map<string, number>();
  graph.edges.forEach((edge, i) => {
    if (routes[i] === "spine" || !byslug.has(edge.from)) return;
    sideTotalPerSource.set(edge.from, (sideTotalPerSource.get(edge.from) ?? 0) + 1);
  });
  const edges: LayoutEdge[] = [];

  graph.edges.forEach((edge, index) => {
    const source = byslug.get(edge.from);
    if (!source) return; // an edge from a phase that isn't in the document
    const target = edge.to ? byslug.get(edge.to) ?? null : null;
    const route = routeOf(source, target);

    let path: string;
    let labelX: number;
    let labelY: number;
    let labelAnchor: LayoutEdge["labelAnchor"] = "middle";

    if (route === "spine") {
      const spine = spinePath(source, target!);
      path = spine.path;
      labelX = spine.labelX;
      labelY = spine.labelY;
    } else {
      const onLeft = route === "back";
      if (route === "dangling") {
        path = danglingPath(source);
      } else if (route === "self") {
        path = selfPath(source, spineRight + rightOffset);
      } else if (route === "forward") {
        const lane = laneByEdgeIndex.get(index) ?? 1;
        path = orthogonalPath(
          source, target!, spineRight + rightOffset + lane * LANE_GAP, "right");
      } else {
        const lane = laneByEdgeIndex.get(index) ?? 1;
        path = orthogonalPath(
          source, target!, leftPad - leftOffset - lane * LANE_GAP, "left");
      }

      const seen = sideLabelsPerSource.get(edge.from) ?? 0;
      sideLabelsPerSource.set(edge.from, seen + 1);
      const total = sideTotalPerSource.get(edge.from) ?? 1;
      labelY =
        source.y + source.h / 2 + (seen - (total - 1) / 2) * LABEL_STAGGER;
      labelX = onLeft ? source.x - LABEL_GAP : source.x + source.w + LABEL_GAP;
      labelAnchor = onLeft ? "end" : "start";
    }

    edges.push({
      key: `${edge.from}->${edge.to ?? "?"}#${index}`,
      from: edge.from,
      to: edge.to,
      label: edge.label,
      description: edge.description,
      kind: edge.kind,
      priority: edge.priority,
      setStatus: edge.setStatus,
      requiresHumanApproval: edge.requiresHumanApproval,
      dangling: edge.dangling,
      route,
      category: CATEGORY_BY_ROUTE[route],
      path,
      labelX,
      labelY,
      labelAnchor,
    });
  });

  // Canvas covers the derived frame AND every node, so a node dragged past
  // either edge grows the scrollable area instead of clipping.
  const derivedHeight =
    MARGIN * 2 + layers.length * NODE_H + Math.max(0, layers.length - 1) * V_GAP;
  const derivedWidth = leftPad + contentW + rightPad;
  const width = Math.max(derivedWidth, ...nodes.map((n) => n.x + n.w + MARGIN));
  const height = Math.max(derivedHeight, ...nodes.map((n) => n.y + n.h + MARGIN));

  return { width, height, nodes, edges };
}

function routeOf(
  source: { slug: string; layer: number },
  target: { slug: string; layer: number } | null,
): EdgeRoute {
  if (!target) return "dangling";
  if (target.slug === source.slug) return "self";
  if (target.layer === source.layer + 1) return "spine";
  return target.layer > source.layer ? "forward" : "back";
}

/** Pre-pass: the routes only, so padding can be reserved before positioning. */
function classifyRoutes(graph: StrategyGraph, layers: GraphNode[][]): EdgeRoute[] {
  const layerOf = new Map<string, number>();
  layers.forEach((layer, index) => layer.forEach((n) => layerOf.set(n.slug, index)));
  return graph.edges.map((edge) => {
    const from = layerOf.get(edge.from);
    if (from === undefined) return "dangling";
    const to = edge.to === null ? undefined : layerOf.get(edge.to);
    if (to === undefined) return "dangling";
    if (edge.to === edge.from) return "self";
    if (to === from + 1) return "spine";
    return to > from ? "forward" : "back";
  });
}

function countLanes(routes: EdgeRoute[], route: EdgeRoute): number {
  return routes.filter((r) => r === route).length;
}

function hasRoute(routes: EdgeRoute[], route: EdgeRoute): boolean {
  return routes.some((r) => r === route);
}

/** Widest estimated label among the edges routed on the given side. */
function widestLabel(
  graph: StrategyGraph,
  routes: EdgeRoute[],
  sides: readonly EdgeRoute[],
): number {
  return graph.edges.reduce((max, edge, i) => {
    const route = routes[i];
    return route !== undefined && sides.includes(route)
      ? Math.max(max, estimateLabelWidth(edge))
      : max;
  }, 0);
}

/** Straight drop between consecutive phases. */
function spinePath(source: LayoutNode, target: LayoutNode) {
  const x1 = source.x + source.w / 2;
  const y1 = source.y + source.h;
  const x2 = target.x + target.w / 2;
  const y2 = target.y;
  const path =
    x1 === x2
      ? `M ${x1} ${y1} L ${x2} ${y2}`
      : `M ${x1} ${y1} C ${x1} ${y1 + V_GAP / 2}, ${x2} ${y2 - V_GAP / 2}, ${x2} ${y2}`;
  return { path, labelX: (x1 + x2) / 2, labelY: y1 + V_GAP / 2 };
}

/**
 * Skip-ahead or go-back transition, routed out to a side lane as a right-angled
 * path with rounded corners. Orthogonal beats a long S-curve here: with a dozen
 * transitions in flight, curves cross at shallow angles and become impossible to
 * follow, while right angles stay legible because each run is axis-aligned.
 */
function orthogonalPath(
  source: LayoutNode,
  target: LayoutNode,
  laneX: number,
  side: "left" | "right",
): string {
  const sx = side === "right" ? source.x + source.w : source.x;
  const sy = source.y + source.h / 2;
  const tx = side === "right" ? target.x + target.w : target.x;
  const ty = target.y + target.h / 2;
  const outward = side === "right" ? 1 : -1;
  const downward = ty > sy ? 1 : -1;
  const r = Math.min(CORNER_R, Math.abs(ty - sy) / 2);

  return [
    `M ${sx} ${sy}`,
    `L ${laneX - outward * r} ${sy}`,
    `Q ${laneX} ${sy} ${laneX} ${sy + downward * r}`,
    `L ${laneX} ${ty - downward * r}`,
    `Q ${laneX} ${ty} ${laneX - outward * r} ${ty}`,
    `L ${tx} ${ty}`,
  ].join(" ");
}

/** A phase that transitions to itself — a bracket beside it. */
function selfPath(source: LayoutNode, laneX: number): string {
  const x = source.x + source.w;
  const yTop = source.y + source.h * 0.3;
  const yBottom = source.y + source.h * 0.7;
  const r = Math.min(CORNER_R, (yBottom - yTop) / 2);
  return [
    `M ${x} ${yTop}`,
    `L ${laneX - r} ${yTop}`,
    `Q ${laneX} ${yTop} ${laneX} ${yTop + r}`,
    `L ${laneX} ${yBottom - r}`,
    `Q ${laneX} ${yBottom} ${laneX - r} ${yBottom}`,
    `L ${x} ${yBottom}`,
  ].join(" ");
}

/**
 * A transition whose target does not exist. Drawn as a stub so the operator can
 * see *which* phase carries the broken rule rather than only reading an error.
 */
function danglingPath(source: LayoutNode): string {
  const x = source.x + source.w;
  const y = source.y + source.h / 2;
  return `M ${x} ${y} L ${x + 56} ${y}`;
}
